import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import DatePicker from '../components/common/DatePicker';
import { Agendamento, StatusAgendamento, TipoProduto } from '../types';

// Dados mock para agendamentos
const mockAgendamentos: Agendamento[] = [
  {
    id: '1',
    usuario_id: '1',
    filial_id: 'FILIAL_1',
    data: '2024-04-17',
    hora: '09:00',
    status: 'confirmado' as StatusAgendamento,
    tipo_produto: 'resina' as TipoProduto,
    observacoes: 'Cliente solicitou agendamento urgente',
    cliente_nome: 'Transportadora XYZ',
    cliente_telefone: '(11) 99999-8888',
    cliente_documento: '12.345.678/0001-90',
    placa_veiculo: 'ABC-1234',
    motorista_nome: 'João Motorista',
    motorista_cpf: '123.456.789-00',
    created_at: '2024-04-17T08:00:00Z',
    updated_at: '2024-04-17T08:00:00Z'
  },
  {
    id: '2',
    usuario_id: '1',
    filial_id: 'FILIAL_1',
    data: '2024-04-17',
    hora: '14:00',
    status: 'pendente' as StatusAgendamento,
    tipo_produto: 'poliuretano' as TipoProduto,
    observacoes: 'Primeiro agendamento do cliente',
    cliente_nome: 'Distribuidora ABC',
    cliente_telefone: '(11) 97777-6666',
    cliente_documento: '98.765.432/0001-10',
    placa_veiculo: 'DEF-5678',
    motorista_nome: 'Pedro Motorista',
    motorista_cpf: '987.654.321-00',
    created_at: '2024-04-17T10:00:00Z',
    updated_at: '2024-04-17T10:00:00Z'
  },
  {
    id: '3',
    usuario_id: '2',
    filial_id: 'FILIAL_2',
    data: '2024-04-18',
    hora: '10:30',
    status: 'cancelado' as StatusAgendamento,
    tipo_produto: 'resina' as TipoProduto,
    observacoes: 'Cancelado pelo cliente',
    cliente_nome: 'Logística Rápida',
    cliente_telefone: '(21) 95555-4444',
    cliente_documento: '45.678.912/0001-23',
    placa_veiculo: 'GHI-9012',
    motorista_nome: 'Carlos Motorista',
    motorista_cpf: '456.789.123-00',
    created_at: '2024-04-16T15:00:00Z',
    updated_at: '2024-04-17T07:00:00Z'
  }
];

const Agendamentos: React.FC = () => {
  const { user } = useAuth();
  const { filialAtual } = useApp();
  const [agendamentos, setAgendamentos] = useState<Agendamento[]>([]);
  const [filteredAgendamentos, setFilteredAgendamentos] = useState<Agendamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAgendamento, setEditingAgendamento] = useState<Agendamento | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    data: '',
    cliente: ''
  });

  useEffect(() => {
    // Simular carregamento de dados
    setTimeout(() => {
      setAgendamentos(mockAgendamentos);
      setFilteredAgendamentos(mockAgendamentos);
      setLoading(false);
    }, 500);
  }, []);

  useEffect(() => {
    // Aplicar filtros
    let filtered = agendamentos;

    if (filters.status) {
      filtered = filtered.filter(a => a.status === filters.status);
    }

    if (filters.data) {
      filtered = filtered.filter(a => a.data === filters.data);
    }

    if (filters.cliente) {
      filtered = filtered.filter(a => 
        a.cliente_nome.toLowerCase().includes(filters.cliente.toLowerCase())
      );
    }

    setFilteredAgendamentos(filtered);
  }, [filters, agendamentos]);

  const handleNewAgendamento = () => {
    setEditingAgendamento(null);
    setShowModal(true);
  };

  const handleEditAgendamento = (agendamento: Agendamento) => {
    setEditingAgendamento(agendamento);
    setShowModal(true);
  };

  const handleDeleteAgendamento = (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este agendamento?')) {
      setAgendamentos(prev => prev.filter(a => a.id !== id));
    }
  };

  const handleSaveAgendamento = (data: any) => {
    if (editingAgendamento) {
      // Editar
      setAgendamentos(prev => prev.map(a => 
        a.id === editingAgendamento.id 
          ? { ...a, ...data, updated_at: new Date().toISOString() }
          : a
      ));
    } else {
      // Novo
      const newAgendamento: Agendamento = {
        id: (agendamentos.length + 1).toString(),
        usuario_id: user?.id || '1',
        filial_id: filialAtual,
        data: data.data,
        hora: data.hora,
        status: 'pendente',
        tipo_produto: data.tipo_produto,
        observacoes: data.observacoes,
        cliente_nome: data.cliente_nome,
        cliente_telefone: data.cliente_telefone,
        cliente_documento: data.cliente_documento,
        placa_veiculo: data.placa_veiculo,
        motorista_nome: data.motorista_nome,
        motorista_cpf: data.motorista_cpf,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setAgendamentos(prev => [...prev, newAgendamento]);
    }
    setShowModal(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmado': return 'bg-green-100 text-green-800';
      case 'pendente': return 'bg-yellow-100 text-yellow-800';
      case 'cancelado': return 'bg-red-100 text-red-800';
      case 'concluido': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Agendamentos</h1>
          <Button onClick={handleNewAgendamento}>
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Agendamento
          </Button>
        </div>

        {/* Filtros */}
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Todos</option>
                <option value="pendente">Pendente</option>
                <option value="confirmado">Confirmado</option>
                <option value="cancelado">Cancelado</option>
                <option value="concluido">Concluído</option>
              </select>
            </div>
            <div>
              <DatePicker
                label="Data"
                value={filters.data}
                onChange={(date) => setFilters(prev => ({ ...prev, data: date ? date.toISOString().split('T')[0] : '' }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
              <Input
                placeholder="Buscar cliente..."
                value={filters.cliente}
                onChange={(value) => setFilters(prev => ({ ...prev, cliente: value }))}
              />
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => setFilters({ status: '', data: '', cliente: '' })}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data/Hora
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Placa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAgendamentos.map((agendamento) => (
                  <tr key={agendamento.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(agendamento.data).toLocaleDateString('pt-BR')} {agendamento.hora}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{agendamento.cliente_nome}</div>
                        <div className="text-gray-500">{agendamento.cliente_telefone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {agendamento.tipo_produto}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {agendamento.placa_veiculo}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(agendamento.status)}`}>
                        {agendamento.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditAgendamento(agendamento)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteAgendamento(agendamento.id)}
                        >
                          Excluir
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredAgendamentos.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">Nenhum agendamento encontrado</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingAgendamento ? 'Editar Agendamento' : 'Novo Agendamento'}
        size="lg"
      >
        <AgendamentoForm
          agendamento={editingAgendamento}
          onSave={handleSaveAgendamento}
          onCancel={() => setShowModal(false)}
        />
      </Modal>
    </div>
  );
};

// Formulário de Agendamento
interface AgendamentoFormProps {
  agendamento: Agendamento | null;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const AgendamentoForm: React.FC<AgendamentoFormProps> = ({ agendamento, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    data: agendamento?.data || '',
    hora: agendamento?.hora || '',
    tipo_produto: agendamento?.tipo_produto || 'resina',
    cliente_nome: agendamento?.cliente_nome || '',
    cliente_telefone: agendamento?.cliente_telefone || '',
    cliente_documento: agendamento?.cliente_documento || '',
    placa_veiculo: agendamento?.placa_veiculo || '',
    motorista_nome: agendamento?.motorista_nome || '',
    motorista_cpf: agendamento?.motorista_cpf || '',
    observacoes: agendamento?.observacoes || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};

    if (!formData.data) newErrors.data = 'Data é obrigatória';
    if (!formData.hora) newErrors.hora = 'Hora é obrigatória';
    if (!formData.cliente_nome) newErrors.cliente_nome = 'Nome do cliente é obrigatório';
    if (!formData.placa_veiculo) newErrors.placa_veiculo = 'Placa é obrigatória';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="Data"
            type="date"
            value={formData.data}
            onChange={(value) => handleChange('data', value)}
            error={errors.data}
            required
          />
        </div>
        <div>
          <Input
            label="Hora"
            type="time"
            value={formData.hora}
            onChange={(value) => handleChange('hora', value)}
            error={errors.hora}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Produto</label>
        <select
          value={formData.tipo_produto}
          onChange={(e) => handleChange('tipo_produto', e.target.value)}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        >
          <option value="resina">Resina</option>
          <option value="poliuretano">Poliuretano</option>
          <option value="outro">Outro</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="Nome do Cliente"
            value={formData.cliente_nome}
            onChange={(value) => handleChange('cliente_nome', value)}
            error={errors.cliente_nome}
            required
          />
        </div>
        <div>
          <Input
            label="Telefone"
            value={formData.cliente_telefone}
            onChange={(value) => handleChange('cliente_telefone', value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="Documento (CNPJ/CPF)"
            value={formData.cliente_documento}
            onChange={(value) => handleChange('cliente_documento', value)}
          />
        </div>
        <div>
          <Input
            label="Placa do Veículo"
            value={formData.placa_veiculo}
            onChange={(value) => handleChange('placa_veiculo', value)}
            error={errors.placa_veiculo}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="Nome do Motorista"
            value={formData.motorista_nome}
            onChange={(value) => handleChange('motorista_nome', value)}
          />
        </div>
        <div>
          <Input
            label="CPF do Motorista"
            value={formData.motorista_cpf}
            onChange={(value) => handleChange('motorista_cpf', value)}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
        <textarea
          value={formData.observacoes}
          onChange={(e) => handleChange('observacoes', e.target.value)}
          rows={3}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit">
          {agendamento ? 'Atualizar' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
};

export default Agendamentos;
